import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import { FaChevronDown, FaTriangleExclamation } from "react-icons/fa6";
import { useUserContext } from "../../context/UserContext";
import DeviceIdInput from "../DeviceIdInput";
import SelectScenario from "./SelectScenario";
import DropdownMenu from "./DropdownMenu.jsx";
import { getTestStartAlert } from "./testStartAlerts";

export default function HeaderPanel({ onViewHistory }) {
  const {
    deviceId,
    setDeviceId,
    scenario,
    subscenario,
    isTesting,
    flushDuration,
    setFlushDuration,
    startTesting,
    stopTesting,
  } = useUserContext();
  const [datasetLocation, setDatasetLocation] = useState("development");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const datasetRef = useRef(null);

  function changeDatasetLocation(nextLocation) {
    setDatasetLocation(nextLocation);
    setIsDropdownOpen(false);
  }

  useEffect(() => {
    if (isTesting) {
      setIsDropdownOpen(false);
    }
  }, [isTesting]);

  function confirmAndStart(alert) {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="rounded-xl border border-amber-300 bg-white shadow-2xl w-[380px] max-w-[92vw] overflow-hidden">
          <div className="flex items-center gap-3 bg-amber-50 px-5 py-4 border-b border-amber-200">
            <FaTriangleExclamation className="text-amber-500 text-xl flex-shrink-0" />
            <h3 className="text-base font-bold text-amber-800">{alert.title}</h3>
          </div>
          <div className="px-5 py-3 text-sm text-gray-700">{alert.message}</div>
          <div className="flex justify-end gap-3 px-5 py-3 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg font-semibold text-gray-600 bg-gray-200 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onClose();
                startTesting(deviceId, scenario, subscenario, datasetLocation);
              }}
              className="px-4 py-2 rounded-lg font-semibold text-white bg-sky-500 hover:bg-sky-700"
            >
              Start
            </button>
          </div>
        </div>
      ),
    });
  }

  function start() {
    if (!deviceId || deviceId.trim() === "" || deviceId.length < 8) {
      window.alert("Enter Valid Device ID");
      return;
    }
    const startAlert = getTestStartAlert(scenario);
    if (startAlert) {
      confirmAndStart(startAlert);
      return;
    }
    startTesting(deviceId, scenario, subscenario, datasetLocation);
  }

  function stop() {
    stopTesting();
  }

  function editFlushDuration() {
    const res = prompt("Enter flush duration in seconds (leave empty for auto)");
    if (res == null) return;
    if (res.trim() === "") setFlushDuration(-1);
    else if (isNaN(parseInt(res, 10)) || parseInt(res, 10) <= 0)
      toast.error("Invalid duration for flushing");
    else setFlushDuration(parseInt(res, 10));
  }

  return (
    <div className="w-full min-w-0">
      <div className="flex flex-wrap gap-3 justify-center items-end w-full overflow-visible">
        <DeviceIdInput deviceId={deviceId} setDeviceId={setDeviceId} />

        <div id="dropdown" className="relative w-24 shrink-0" ref={datasetRef}>
          <button
            disabled={isTesting}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex gap-2 justify-between items-center py-2 px-4 w-full font-bold text-white bg-indigo-500 rounded hover:bg-indigo-700 disabled:bg-gray-500"
          >
            <span className="capitalize">
              {datasetLocation === "development" ? "Dev" : "Prod"}
            </span>
            <FaChevronDown />
          </button>
          <DropdownMenu
            open={isDropdownOpen}
            anchorRef={datasetRef}
            onClose={() => setIsDropdownOpen(false)}
          >
            <li
              className="py-1 hover:bg-gray-200"
              onClick={() => changeDatasetLocation("development")}
            >
              Dev
            </li>
            <li
              className="py-1 hover:bg-gray-200"
              onClick={() => changeDatasetLocation("production")}
            >
              Prod
            </li>
          </DropdownMenu>
        </div>

        <SelectScenario />

        {scenario === "check_request" && (
          <div className="flex flex-col items-center shrink-0">
            <span className="mb-1 text-xs font-medium text-gray-600 whitespace-nowrap">
              Flush Duration
            </span>
            <button
              type="button"
              disabled={isTesting}
              onClick={editFlushDuration}
              className="py-2 px-3 min-w-[5rem] font-semibold text-white bg-sky-500 rounded hover:bg-sky-700 disabled:bg-gray-500"
            >
              {flushDuration === -1 ? "auto" : `${flushDuration}s`}
            </button>
          </div>
        )}

        <button
          onClick={isTesting ? stop : start}
          className="py-2 px-4 font-bold text-white rounded disabled:bg-gray-500 bg-sky-500 hover:bg-sky-700 shrink-0"
        >
          {isTesting ? "Stop" : "Start"}
        </button>

        <button
          type="button"
          onClick={onViewHistory}
          className="py-2 px-4 font-semibold text-indigo-500 rounded border-2 border-indigo-500 transition hover:text-white hover:bg-indigo-500 disabled:bg-gray-500 shrink-0"
        >
          View History
        </button>
      </div>
    </div>
  );
}
